import React, { useState } from 'react';

/**
 * MoneyProtectionCard — explains CoreBids escrow / milestone model to homeowners
 * in plain language. Renders as a collapsible side panel card or inline block.
 */

interface MoneyProtectionCardProps {
  variant?: 'card' | 'banner'; // card = side panel, banner = inline full-width
  className?: string;
  style?: React.CSSProperties;
}

const steps = [
  {
    number: '1',
    title: 'You fund escrow',
    description:
      'Your payment is held securely in escrow — not paid directly to the contractor — until each milestone is verified and released.',
  },
  {
    number: '2',
    title: 'Contractor submits evidence',
    description:
      'After completing each phase, the contractor uploads photos and documentation for milestone review.',
  },
  {
    number: '3',
    title: 'CoreBids reviews and releases',
    description:
      'CoreBids reviews each milestone submission and releases that portion of the payment to the contractor once the work is verified.',
  },
  {
    number: '4',
    title: 'Dispute any milestone',
    description:
      'If the work does not match what was agreed, you can dispute the milestone before payment is released. CoreBids reviews disputes and holds funds until resolved.',
  },
];

const MoneyProtectionCard: React.FC<MoneyProtectionCardProps> = ({
  variant = 'card',
  className,
  style,
}) => {
  const [expanded, setExpanded] = useState(variant === 'banner');

  if (variant === 'banner') {
    return (
      <div
        className={className}
        style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 12,
          padding: '20px 24px',
          ...style,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#14532d' }}>
            How your money is protected
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          {steps.map(step => (
            <div key={step.number} style={{ display: 'flex', gap: 10 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: '#16a34a',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {step.number}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#14532d', marginBottom: 2 }}>
                  {step.title}
                </div>
                <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.4 }}>
                  {step.description}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#4b7c5a' }}>
          Questions about escrow or payments?{' '}
          <a href="mailto:support@corebids.com" style={{ color: '#16a34a', fontWeight: 600 }}>
            Contact support
          </a>
        </p>
      </div>
    );
  }

  // Card variant (collapsible side panel)
  return (
    <div
      className={className}
      style={{
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#14532d' }}>
            How your money is protected
          </span>
        </div>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#16a34a"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {steps.map(step => (
              <div key={step.number} style={{ display: 'flex', gap: 10 }}>
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#16a34a',
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {step.number}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#14532d', marginBottom: 2 }}>
                    {step.title}
                  </div>
                  <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.4 }}>
                    {step.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 11, color: '#4b7c5a' }}>
            Questions?{' '}
            <a href="mailto:support@corebids.com" style={{ color: '#16a34a', fontWeight: 600 }}>
              support@corebids.com
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default MoneyProtectionCard;
